import type { Card } from '@/hooks/useCards'
import GameBadge from '@/components/ui/GameBadge'
import ImageUploadBox from '@/components/ui/ImageUploadBox'
import {
  CARD_CONDITION_OPTIONS,
  LISTING_TYPES,
  SALE_TYPES,
  SEALED_CONDITION_OPTIONS,
  SEALED_PRODUCT_OPTIONS,
  getListingType,
  getSaleType,
} from '@/lib/games'
import {Loader2, Package, RectangleVertical, X} from 'lucide-react'
import { useEffect, useState } from 'react'

interface CardFormModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: Partial<Card>) => Promise<void>
  initial?: Card | null
}

const emptyForm = {
  title: '',
  listingType: 'card' as string,
  productType: 'Booster Box' as string,
  condition: 'Near Mint' as string,
  imageUrl: '',
  price: '',
  description: '',
  tcgplayerUrl: '',
  ebayUrl: '',
  saleType: 'fixed' as string,
  forSale: true,
}

function CardFormModal({ open, onClose, onSubmit, initial }: CardFormModalProps) {
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const listingType = getListingType(form.listingType)
  const isSealed = listingType === 'sealed'
  const conditionOptions = isSealed ? SEALED_CONDITION_OPTIONS : CARD_CONDITION_OPTIONS

  useEffect(() => {
    if (!open) return
    if (initial) {
      const nextType = getListingType(initial.listingType)
      setForm({
        title: initial.title || '',
        listingType: nextType,
        productType: initial.productType || 'Booster Box',
        condition: initial.condition || (nextType === 'sealed' ? 'Factory Sealed' : 'Near Mint'),
        imageUrl: initial.imageUrl || '',
        price: initial.price != null ? String(initial.price) : '',
        description: initial.description || '',
        tcgplayerUrl: initial.tcgplayerUrl || '',
        ebayUrl: initial.ebayUrl || '',
        saleType: getSaleType(initial.saleType),
        forSale: initial.forSale ?? true,
      })
    } else {
      setForm(emptyForm)
    }
  }, [open, initial])

  if (!open) return null

  const setListingType = (nextType: string) => {
    setForm(f => ({
      ...f,
      listingType: nextType,
      condition: nextType === 'sealed' ? 'Factory Sealed' : 'Near Mint',
      productType: f.productType || 'Booster Box',
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim() || !form.imageUrl) return
    setSaving(true)
    try {
      await onSubmit({
        title: form.title.trim(),
        game: 'Pokemon',
        listingType,
        productType: isSealed ? form.productType : undefined,
        condition: form.condition,
        imageUrl: form.imageUrl,
        price: form.price ? Number(form.price) : undefined,
        description: form.description.trim(),
        tcgplayerUrl: form.tcgplayerUrl.trim(),
        ebayUrl: form.ebayUrl.trim(),
        saleType: getSaleType(form.saleType),
        forSale: form.forSale,
      })
      onClose()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/80 px-3 py-4 backdrop-blur-sm sm:items-center sm:px-4 sm:py-8">
      <div className="relative mb-16 max-h-[calc(100dvh-6rem)] w-full max-w-lg overflow-y-auto rounded-[32px] border-4 border-[#3B4CCA] bg-[#111111] p-5 shadow-[10px_10px_0_rgba(255,222,0,0.18)] sm:mb-0 sm:p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-white/50 hover:bg-white/5 hover:text-white transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="font-display mb-5 text-2xl font-extrabold text-white">
          {initial ? 'Edit listing' : 'Add a Pokemon listing'}
        </h2>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">What are you posting?</label>
            <div className="grid grid-cols-2 gap-2">
              {LISTING_TYPES.map(option => {
                const active = listingType === option.value
                const Icon = option.value === 'sealed' ? Package : RectangleVertical
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setListingType(option.value)}
                    className={`flex items-center justify-center gap-2 rounded-2xl border-2 px-3 py-3 text-sm font-extrabold transition-transform hover:-translate-y-0.5 ${
                      active
                        ? option.value === 'sealed'
                          ? 'border-[#3B4CCA] bg-[#3B4CCA] text-white shadow-[0_4px_0_#24308a]'
                          : 'border-[#FFDE00] bg-[#FFDE00] text-black shadow-[0_4px_0_#c9a800]'
                        : 'border-white/15 bg-white/5 text-white/65'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {option.label}
                  </button>
                )
              })}
            </div>
          </div>

          <ImageUploadBox
            label={isSealed ? 'Product photo' : 'Card photo'}
            value={form.imageUrl}
            onChange={url => setForm(f => ({ ...f, imageUrl: url }))}
          />

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">
              {isSealed ? 'Product name' : 'Card name'}
            </label>
            <input
              type="text"
              required
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder={isSealed ? 'e.g. Surging Sparks Booster Box' : 'e.g. Charizard VMAX Rainbow Rare'}
              className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
            />
          </div>

          <div className={`grid gap-3 ${isSealed ? 'grid-cols-2' : ''}`}>
            {isSealed ? (
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-white/60">Sealed type</label>
                <select
                  value={form.productType}
                  onChange={e => setForm(f => ({ ...f, productType: e.target.value }))}
                  className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
                >
                  {SEALED_PRODUCT_OPTIONS.map(option => (
                    <option key={option} value={option} className="bg-[#111111]">{option}</option>
                  ))}
                </select>
              </div>
            ) : null}
            <div>
              <label className="mb-1.5 block text-xs font-semibold text-white/60">Condition</label>
              <select
                value={form.condition}
                onChange={e => setForm(f => ({ ...f, condition: e.target.value }))}
                className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
              >
                {conditionOptions.map(option => (
                  <option key={option} value={option} className="bg-[#111111]">{option}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">Sale type</label>
            <div className="grid grid-cols-2 gap-2">
              {SALE_TYPES.map(option => {
                const active = getSaleType(form.saleType) === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, saleType: option.value }))}
                    className={`rounded-2xl border-2 px-3 py-2.5 text-sm font-extrabold transition-transform hover:-translate-y-0.5 ${
                      active
                        ? option.value === 'auction'
                          ? 'border-[#FF1A1A] bg-[#FF1A1A] text-white shadow-[0_4px_0_#9d1010]'
                          : 'border-[#FFDE00] bg-[#FFDE00] text-black shadow-[0_4px_0_#c9a800]'
                        : 'border-white/15 bg-white/5 text-white/65'
                    }`}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {getSaleType(form.saleType) === 'fixed' ? (
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-white/60">Price (USD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                  placeholder="0.00"
                  className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
                />
              </div>
            ) : (
              <div className="flex items-end">
                <p className="pb-2 text-xs font-semibold text-white/45">This listing will show Auction on your profile grid.</p>
              </div>
            )}
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-sm font-semibold text-white/80">
                <input
                  type="checkbox"
                  checked={form.forSale}
                  onChange={e => setForm(f => ({ ...f, forSale: e.target.checked }))}
                  className="h-4 w-4 rounded border-white/20 bg-white/[0.03] accent-[#FFDE00]"
                />
                Currently for sale
              </label>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">TCGplayer link</label>
            <input
              type="url"
              value={form.tcgplayerUrl}
              onChange={e => setForm(f => ({ ...f, tcgplayerUrl: e.target.value }))}
              placeholder="https://www.tcgplayer.com/..."
              className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">eBay link</label>
            <input
              type="url"
              value={form.ebayUrl}
              onChange={e => setForm(f => ({ ...f, ebayUrl: e.target.value }))}
              placeholder="https://www.ebay.com/..."
              className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-white/60">Notes (optional)</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder={isSealed ? 'Set, language, or anything buyers should know' : 'Set, rarity, grading, or anything buyers should know'}
              rows={2}
              className="w-full rounded-2xl border-2 border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm font-semibold text-white placeholder:text-white/25 outline-none focus:border-[#FFDE00] focus:ring-2 focus:ring-[#FFDE00]/20 resize-none"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <GameBadge listingType={listingType} productType={isSealed ? form.productType : undefined} />
          </div>

          <button
            type="submit"
            disabled={saving || !form.title.trim() || !form.imageUrl}
            className="mt-1 flex items-center justify-center gap-2 rounded-full bg-[#FFDE00] py-3 text-sm font-extrabold text-black shadow-[0_5px_0_#c9a800] transition-transform hover:-translate-y-1 disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {initial ? 'Save changes' : isSealed ? 'Add sealed product' : 'Add card to store'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default CardFormModal
